// A tab opened before a deploy still runs the old build, whose page chunks are
// gone from the new one (Vercel serves one build's hashed assets). Its next
// navigation to a lazy page imports a chunk that 404s, and that throws: a blank
// page, and since the error boundary a crash page. Days with many deploys made
// that the most common crash there was. Reloading picks up the new build; the
// guard keeps a chunk that is really missing from reloading forever.
import { track } from '../analytics/track';

const KEY = 'cs_stale_build_reload_at';
// What the reload was for, reported once the new build is up: a reload leaves
// no time to send anything, so a whole evening of these was invisible
const PENDING = 'cs_stale_build_reload_pending';
const GUARD_MS = 30000;

// This page has already asked for a reload onto the new build. A chunk error that
// arrives in the moments before the page goes away is part of the same reload,
// not a second failure
let reloadingSince = 0;
export const isReloadingForNewBuild = (now = Date.now()) => reloadingSince > 0 && now - reloadingSince < GUARD_MS;

/** Tests only: a fresh page, as the one after a reload is */
export const __forgetReloadInThisPage = () => { reloadingSince = 0; };

const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError|Loading (CSS )?chunk \S+ failed/i;

export const isChunkLoadError = (error) =>
  !!error && CHUNK_ERROR.test(`${error.name || ''} ${error.message || error}`);

// A chunk can fail because the browser is holding a 404 for it. /assets answers
// everything, 404s included, with `max-age=31536000, immutable` (vercel.json),
// and a reload does not revalidate an immutable subresource. In the minute a
// deploy takes to reach every edge, a new page can ask for its chunk and be
// answered by the old deployment: that 404 is then kept for a year, and the
// page is a crash page on every visit, reloads and all. /login was, in a
// browser here, from the 11:23 deploy on 2026-09-23 while the server had the
// chunk all along. Fetching the chunk with cache: 'reload' replaces what the
// browser holds, so the reload that follows gets the real file.
const ASSET_IN_TEXT = /(?:https?:\/\/[^\s'"()]+?)?\/assets\/[\w.-]+\.(?:js|css)/g;
const REFETCH_MS = 3000;

/** Our own asset files a chunk error names, or that this page was refused. */
export function failedAssetUrls(error) {
  const found = new Set();
  const origin = window.location.origin;
  const ours = (raw) => {
    try {
      const url = new URL(raw, origin);
      if (url.origin === origin && url.pathname.startsWith('/assets/')) found.add(url.href);
    } catch {
      // not a URL
    }
  };
  const text = `${error?.message || error || ''}`;
  for (const match of text.matchAll(ASSET_IN_TEXT)) ours(match[0]);
  // Safari names no file ("Importing a module script failed."); the refused
  // request is still in the page's resource timings where the browser says so
  try {
    for (const entry of window.performance?.getEntriesByType?.('resource') || []) {
      if (entry.responseStatus >= 400) ours(entry.name);
    }
  } catch {
    // no resource timings
  }
  return [...found];
}

function refetch(urls) {
  if (!urls.length || typeof window.fetch !== 'function') return null;
  const fresh = Promise.allSettled(urls.map((url) => window.fetch(url, { cache: 'reload', credentials: 'same-origin' })));
  // Never hold the reload hostage to a request that does not come back
  return Promise.race([fresh, new Promise((resolve) => setTimeout(resolve, REFETCH_MS))]);
}

/** The page with ids taken out, so what happens groups by route, not by project. */
export const pagePath = () => {
  try {
    return window.location.pathname.replace(/[0-9a-f]{8}-[0-9a-f-]{27}/gi, ':id');
  } catch {
    return '';
  }
};

// Reload once. False when this tab already reloaded for it moments ago: then
// the chunk is really missing, and the error should be shown, not looped on.
// `error` is the chunk error: the files it names are fetched afresh first.
export function reloadForNewBuild(now = Date.now(), reason = 'chunk', error = null) {
  try {
    const last = Number(window.sessionStorage.getItem(KEY)) || 0;
    if (now - last < GUARD_MS) return false;
    window.sessionStorage.setItem(KEY, String(now));
    window.sessionStorage.setItem(PENDING, JSON.stringify({ page: pagePath(), reason }));
  } catch {
    // No storage: still reload once; a loop is impossible without the guard
    // only if the reload fixes it, which is the common case
  }
  reloadingSince = now;
  const refetching = refetch(failedAssetUrls(error));
  if (refetching) refetching.then(() => window.location.reload());
  else window.location.reload();
  return true;
}

/**
 * On the new build, say that the last load was a reload onto it. Tracking it
 * before the reload never worked: the event sat in the browser's queue as the
 * page went away, so `app_stale_build_reload` had no events at all while crash
 * pages were being shown minutes after every deploy.
 */
export function reportStaleBuildReload() {
  try {
    const raw = window.sessionStorage.getItem(PENDING);
    if (!raw) return null;
    window.sessionStorage.removeItem(PENDING);
    const info = JSON.parse(raw);
    track('app_stale_build_reload', info);
    return info;
  } catch {
    return null;
  }
}

export function installStaleBuildRecovery() {
  reportStaleBuildReload();
  // Vite fires this when a dynamic import or its preloads fail. Reload, but never
  // preventDefault: Vite then stops rethrowing, the import RESOLVES to undefined,
  // and React.lazy crashes reading .default of it before the reload lands —
  // "Cannot read properties of undefined (reading 'default')", a crash page and
  // an app_crash, twice for one person on 2026-09-23 during a morning of deploys.
  // Left to throw, the chunk error reaches the ErrorBoundary, which shows the
  // quiet reloading state while this reload happens
  window.addEventListener('vite:preloadError', (event) => {
    reloadForNewBuild(Date.now(), 'preload', event.payload);
  });
}
